import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import _ from "lodash";

import { COLORS } from "constant";
import DashboardCreateSelectSensorModal from "components/dashboard/create/modal/SelectDevices";
import {
    ColorPicker,
    useCreateDashboardStore,
} from "components/dashboard/create";
import ChartList from "./ChartList";
import "./styles.css";
import { Device } from "types";

type Props = {
    onChange: (data: any) => void;
    isUpdate?: boolean;
    panel?: any;
    gatewayIds?: string[];
};

/**
 * Renders a form for creating or updating a People Count Sensor chart.
 * Handles sensor selection and updates chart properties based on user input.
 * 
 * @param {Object} params - The parameters for the function.
 * @param {Function} params.onChange - Callback function to handle changes.
 * @param {boolean} params.isUpdate - Flag indicating if it's an update operation.
 * @param {Object} params.panel - The panel object containing chart attributes.
 * @param {Array<string>} params.gatewayIds - List of gateway IDs associated with sensors.
 * @returns {JSX.Element} The rendered form component.
 * @throws {Error} Throws an error if less than 2 sensors are selected.
 */
const CreateCombinedSensorsChart = ({
    onChange,
    isUpdate,
    panel,
    gatewayIds,
}: Props) => {
    const sensorType = "People Count Sensor";
    const defaultChartName = "People Count";
    const errorMessage =
        "Please select at least 2 sensors to create an aggregate count chart.";

    const [showSelectSensorModal, setShowSelectSensorModal] = useState(false);
    const [error, setError] = useState("");
    const [sensorList, setSensorList] = useState<Device[] | null>(
        isUpdate ? null : []
    );
    const [chartColor, setChartColor] = useState(
        isUpdate ? _.get(panel, "attributes[0].color") : COLORS[0]
    );
    const [chartName, setChartName] = useState(
        isUpdate ? panel.name : defaultChartName
    );

    const { deviceList } = useCreateDashboardStore();

    /**
     * Get the list of sensors based on the gateways, just for update case
     */
    useEffect(() => {
        if (
            deviceList.length &&
            isUpdate &&
            !sensorList &&
            gatewayIds?.length
        ) {
            const sensors = deviceList.filter(
                (device: any) => (gatewayIds || []).includes(device.gatewayId)
            );

            setSensorList(sensors);
        }
    }, [deviceList, isUpdate, sensorList, gatewayIds]);

    useEffect(() => {
        if (typeof onChange === "function" && sensorList !== null) {
            const changeData: any = {
                chartName,
                chartColor,
                sensorList,
            };

            onChange(changeData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartName, chartColor, sensorList]);

    const handleSelectSensor = (selectedSensors: Record<string, Device>) => {
        const selectedSensor = Object.values(selectedSensors);
        if (selectedSensor.length === 0) {
            return;
        }

        setSensorList(selectedSensor);

        const error = selectedSensor.length < 2 ? errorMessage : "";
        setError(error);
    };

    if (isUpdate && !deviceList.length) {
        return <></>;
    }

    return (
        <div className="create-pcs-chart-form">
            <Form>
                <Form.Group >
                    <Form.Label htmlFor="widget-name">Widget name</Form.Label>
                    <Form.Control
                        name="widget-name"
                        type="text"
                        id="widget-name"
                        value={chartName}
                        placeholder=""
                        onChange={(event) => setChartName(event.target.value)}
                    />
                </Form.Group>

                <Form.Group className="mt-4">
                    <Form.Label>Color</Form.Label>
                    <ColorPicker
                        defaultValue={chartColor}
                        onChange={(_, color) => {
                            setChartColor(color);
                        }}
                    />
                </Form.Group>
                <hr className="my-4 break-line" />
                <Form.Group className="mt-4">
                    <Form.Label htmlFor="sensor_type" >Select sensor type</Form.Label>
                    <Form.Control id="sensor_type" disabled as="select" custom value={sensorType}>
                        <option value={sensorType}>PCS</option>
                    </Form.Control>
                </Form.Group>
            </Form>
            <Button
                variant="primary"
                onClick={() => {
                    setShowSelectSensorModal(true);
                }}
            >
                Select Sensors
            </Button>

            {error ? (
                <p style={{ color: "#F2495E", marginTop: 10, fontSize: 14 }}>
                    {error}
                </p>
            ) : (
                ""
            )}

            <ChartList
                sensorList={sensorList}
                onChange={(newSensors: Device[]) => {
                    setSensorList(newSensors);

                    if (newSensors.length < 2) {
                        setError(errorMessage)
                    }
                }}
            />

            <DashboardCreateSelectSensorModal
                allowSensorFilter={false}
                show={showSelectSensorModal}
                values={(sensorList ?? []).pluck("deviceId")}
                deviceType="Sensor"
                sensorTypes={[sensorType]}
                onHide={() => setShowSelectSensorModal(false)}
                onSave={(_event, data) => handleSelectSensor(data)}
            />
        </div>
    );
};

export default CreateCombinedSensorsChart;
